export const KEEPER_DRAFT_FIT_STRATEGY = "Balanced" as const;
export const KEEPER_DRAFT_FIT_SEED_VERSION = "league-hub-persistent-fit-v1";

export interface KeeperDraftTeamRoster {
  teamName: string;
  keeperPlayerIds: readonly string[];
}

export interface KeeperDraftFitRequest {
  teamName: string;
  availablePlayerIds: readonly string[];
  teamRosters: readonly KeeperDraftTeamRoster[];
}

export interface KeeperDraftFitRow {
  playerId: string;
  score: number;
  roundedFit: number;
  reasons: string[];
}

export interface KeeperDraftFitRanking {
  strategy: typeof KEEPER_DRAFT_FIT_STRATEGY;
  teamName: string;
  canonicalTeamCount: number;
  currentRosterCount: number;
  rankedPlayerIds: string[];
  rows: KeeperDraftFitRow[];
}

interface DraftLabPlayer {
  id: string;
  name: string;
}

interface DraftLabSourceTeam {
  team: string;
}

export interface DraftLabFitData {
  league: {
    maxKeepers: number;
  };
  players: DraftLabPlayer[];
  teams: DraftLabSourceTeam[];
}

interface DraftLabSimulationTeam {
  name: string;
  initialKeeperIds: string[];
  keeperDraftIds: string[];
  pooledIds: string[];
  finalKeeperIds: string[];
  draftedIds: string[];
  rosterIds: string[];
  [key: string]: unknown;
}

interface DraftLabSimulationState {
  teams: DraftLabSimulationTeam[];
  standardSchedule: Array<{ owner: string }>;
  currentPickIndex: number;
  availableIds: string[];
  selectedIds: string[];
  standardDraftLog: unknown[];
  [key: string]: unknown;
}

interface DraftLabFitResult {
  score: number;
  reasons?: unknown;
  breakdown?: unknown;
}

interface DraftLabFitCandidate extends DraftLabFitResult {
  player: DraftLabPlayer;
}

export interface DraftLabFitEngine {
  simulateLeague: (
    seed: string,
    options: {
      controlledTeamName: string;
      manualInitialKeeperIds: string[];
      keeperDraftMode: "controlled";
    },
  ) => DraftLabSimulationState;
  finalizeStandardDraftState: (
    state: DraftLabSimulationState,
    phase?: string,
  ) => DraftLabSimulationState;
  createAvailablePlayerFitBatchContext: (
    state: DraftLabSimulationState,
    team: DraftLabSimulationTeam,
    strategy: typeof KEEPER_DRAFT_FIT_STRATEGY,
  ) => unknown;
  availablePlayerFitsForState: (
    state: DraftLabSimulationState,
    team: DraftLabSimulationTeam,
    players: DraftLabPlayer[],
    context: unknown,
  ) => DraftLabFitResult[];
  rankCandidatesWithinFavoriteWindow: (
    team: DraftLabSimulationTeam,
    candidates: DraftLabFitCandidate[],
    options: { includeUserTeam: true },
  ) => DraftLabFitCandidate[];
  sortAvailablePlayerCandidates: (
    candidates: DraftLabFitCandidate[],
    sort: { key: "fit"; direction: "desc" },
    queueIds: string[],
  ) => DraftLabFitCandidate[];
}

function requireUniqueStrings(values: readonly string[], label: string): string[] {
  if (!Array.isArray(values) || values.some((value) => typeof value !== "string" || !value)) {
    throw new Error(`${label} must contain only player ids.`);
  }
  const unique = [...new Set(values)];
  if (unique.length !== values.length) throw new Error(`${label} contains a duplicate player.`);
  return unique;
}

/**
 * Builds a synthetic Standard Draft checkpoint from the persistent Keeper
 * Draft's authoritative keeper ledger, then delegates every score and ordering
 * decision to the exact Draft Lab mock Standard Draft engine. The controlled
 * Draft Lab view defaults to Balanced, so the League Hub intentionally does
 * the same rather than introducing a second strategy model.
 */
export function rankPersistentKeeperDraftPoolByFit(
  engine: DraftLabFitEngine,
  data: DraftLabFitData,
  request: KeeperDraftFitRequest,
): KeeperDraftFitRanking {
  if (!request.teamName) throw new Error("The Fit team is missing.");
  const playerById = new Map(data.players.map((player) => [player.id, player]));
  const canonicalTeamNames = data.teams.map((team) => team.team);
  if (
    canonicalTeamNames.length === 0
    || new Set(canonicalTeamNames).size !== canonicalTeamNames.length
  ) {
    throw new Error("The canonical Draft Lab team ledger is invalid.");
  }
  if (!canonicalTeamNames.includes(request.teamName)) {
    throw new Error("The selected Team Beer franchise was not found.");
  }

  if (!Array.isArray(request.teamRosters) || request.teamRosters.length !== canonicalTeamNames.length) {
    throw new Error("The authoritative keeper ledger is incomplete.");
  }
  const rosterByTeam = new Map<string, string[]>();
  const globallyRostered = new Set<string>();
  for (const ledger of request.teamRosters) {
    if (!ledger || typeof ledger.teamName !== "string" || !canonicalTeamNames.includes(ledger.teamName)) {
      throw new Error("The authoritative keeper ledger contains an unknown team.");
    }
    if (rosterByTeam.has(ledger.teamName)) {
      throw new Error("The authoritative keeper ledger contains a duplicate team.");
    }
    const keeperPlayerIds = requireUniqueStrings(
      ledger.keeperPlayerIds,
      `${ledger.teamName}'s keeper ledger`,
    );
    if (keeperPlayerIds.length > data.league.maxKeepers) {
      throw new Error(`${ledger.teamName}'s keeper ledger exceeds the league limit.`);
    }
    for (const playerId of keeperPlayerIds) {
      if (!playerById.has(playerId)) {
        throw new Error("A keeper is missing from the canonical Draft Lab catalog.");
      }
      if (globallyRostered.has(playerId)) {
        throw new Error("The authoritative keeper ledger assigns one player to multiple teams.");
      }
      globallyRostered.add(playerId);
    }
    rosterByTeam.set(ledger.teamName, keeperPlayerIds);
  }
  if (canonicalTeamNames.some((teamName) => !rosterByTeam.has(teamName))) {
    throw new Error("The authoritative keeper ledger is incomplete.");
  }

  const requestedAvailableIds = requireUniqueStrings(
    request.availablePlayerIds,
    "The available-player ledger",
  );
  for (const playerId of requestedAvailableIds) {
    if (!playerById.has(playerId)) {
      throw new Error("An available player is missing from the canonical Draft Lab catalog.");
    }
    if (globallyRostered.has(playerId)) {
      throw new Error("A player cannot be both rostered and available.");
    }
  }

  // The Draft Lab's default Fit order begins in canonical DATA.players order;
  // preserve that source order so exact-score ties resolve identically even if
  // an API response happens to list the same pool in a different order.
  const availableSet = new Set(requestedAvailableIds);
  const availablePlayers = data.players.filter((player) => availableSet.has(player.id));
  if (availablePlayers.length !== requestedAvailableIds.length) {
    throw new Error("The available-player ledger could not be resolved exactly.");
  }

  const currentRoster = rosterByTeam.get(request.teamName) ?? [];
  const simulation = engine.simulateLeague(
    `${KEEPER_DRAFT_FIT_SEED_VERSION}|${request.teamName}`,
    {
      controlledTeamName: request.teamName,
      manualInitialKeeperIds: [...currentRoster],
      keeperDraftMode: "controlled",
    },
  );
  for (const team of simulation.teams) {
    const keeperPlayerIds = rosterByTeam.get(team.name);
    if (!keeperPlayerIds) throw new Error("The Draft Lab simulation omitted a Team Beer franchise.");
    team.initialKeeperIds = [...keeperPlayerIds];
    team.keeperDraftIds = [];
    team.pooledIds = [];
    team.finalKeeperIds = [...keeperPlayerIds];
    team.draftedIds = [];
    team.rosterIds = [...keeperPlayerIds];
  }
  engine.finalizeStandardDraftState(simulation, "review");
  simulation.availableIds = availablePlayers.map((player) => player.id);
  simulation.selectedIds = [...globallyRostered];
  simulation.standardDraftLog = [];
  const firstControlledPick = simulation.standardSchedule.findIndex(
    (pick) => pick.owner === request.teamName,
  );
  simulation.currentPickIndex = firstControlledPick >= 0 ? firstControlledPick : 0;

  const team = simulation.teams.find((candidate) => candidate.name === request.teamName);
  if (!team) throw new Error("The Draft Lab simulation omitted the selected franchise.");
  const context = engine.createAvailablePlayerFitBatchContext(
    simulation,
    team,
    KEEPER_DRAFT_FIT_STRATEGY,
  );
  const fitRows = engine.availablePlayerFitsForState(
    simulation,
    team,
    availablePlayers,
    context,
  );
  if (fitRows.length !== availablePlayers.length) {
    throw new Error("The Draft Lab Fit engine returned an incomplete ranking.");
  }
  const candidates = availablePlayers
    .map((player, index): DraftLabFitCandidate => ({
      player,
      ...(fitRows[index] as DraftLabFitResult),
    }))
    .sort((left, right) => right.score - left.score);
  if (candidates.some((candidate) => !Number.isFinite(candidate.score))) {
    throw new Error("The Draft Lab Fit engine returned a non-finite score.");
  }
  const personalized = engine.rankCandidatesWithinFavoriteWindow(
    team,
    candidates,
    { includeUserTeam: true },
  );
  const ranked = engine.sortAvailablePlayerCandidates(
    personalized,
    { key: "fit", direction: "desc" },
    [],
  );
  const rankedPlayerIds = ranked.map((candidate) => candidate.player.id);
  if (
    rankedPlayerIds.length !== requestedAvailableIds.length
    || new Set(rankedPlayerIds).size !== rankedPlayerIds.length
    || rankedPlayerIds.some((playerId) => !availableSet.has(playerId))
  ) {
    throw new Error("The Draft Lab Fit engine returned an invalid ranking.");
  }

  return {
    strategy: KEEPER_DRAFT_FIT_STRATEGY,
    teamName: request.teamName,
    canonicalTeamCount: canonicalTeamNames.length,
    currentRosterCount: currentRoster.length,
    rankedPlayerIds,
    rows: ranked.map((candidate) => ({
      playerId: candidate.player.id,
      score: candidate.score,
      roundedFit: Math.round(candidate.score),
      reasons: Array.isArray(candidate.reasons)
        ? candidate.reasons.filter((reason): reason is string => typeof reason === "string")
        : [],
    })),
  };
}
